import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Box, Eye, EyeOff } from 'lucide-react'
import { invitePublicApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

/**
 * Public /accept-invite/:token page.
 *
 * Flow:
 *   GET /invitations/:token returns invite metadata and tells us whether
 *   the email already has a Stovalt account. Branch:
 *     - New email  → ask for name + password, accept creates account + token.
 *     - Existing   → ask them to sign in first (invite link persists in URL),
 *                    then re-hit accept - the sanctum bearer on the request
 *                    tells the backend to run the existing-user branch.
 */
export default function AcceptInvite() {
  const { token }  = useParams()
  const navigate   = useNavigate()
  const { user, updateUser } = useAuth()

  const [status, setStatus]     = useState('loading') // loading | ready | gone | error
  const [invite, setInvite]     = useState(null)
  const [error,  setError]      = useState('')
  const [form, setForm]         = useState({ name: '', password: '', password_confirmation: '' })
  const [showPw, setShowPw]     = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    invitePublicApi.show(token)
      .then(({ data }) => {
        setInvite(data.data)
        setForm(f => ({ ...f, name: data.data.existing_user_name || '' }))
        setStatus('ready')
      })
      .catch(e => {
        const msg = e.response?.data?.message || 'Invitation not found.'
        setError(msg)
        setStatus(e.response?.status === 410 ? 'gone' : 'error')
      })
  }, [token])

  // If the invitee is ALREADY signed in as the invited email, auto-submit
  // the existing-user accept - no need to ask them to confirm the password
  // they've already entered to get here.
  const acceptAsExistingUser = async () => {
    setSubmitting(true); setError('')
    try {
      const { data } = await invitePublicApi.accept(token, {})
      // The backend returns only { user } for existing users - we already have
      // a valid token in localStorage, no re-auth needed. Refresh context so
      // new facilities show up in the switcher.
      if (data.user) updateUser({ ...user, ...data.user })
      navigate('/dashboard')
    } catch (e) {
      setError(e.response?.data?.message || 'Could not accept invitation.')
    } finally { setSubmitting(false) }
  }

  const submitNewUser = async e => {
    e.preventDefault()
    if (form.password !== form.password_confirmation) {
      setError('Passwords do not match.'); return
    }
    setSubmitting(true); setError('')
    try {
      const { data } = await invitePublicApi.accept(token, {
        name: form.name,
        password: form.password,
        password_confirmation: form.password_confirmation,
      })
      if (data.token) {
        localStorage.setItem('token', data.token)
        localStorage.setItem('user',  JSON.stringify(data.user))
        window.location.href = '/dashboard'   // full reload so AuthProvider picks up the new token
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Could not accept invitation.')
    } finally { setSubmitting(false) }
  }

  // ── Render states ─────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <Shell>
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    )
  }

  if (status !== 'ready') {
    return (
      <Shell>
        <h1 className="font-display text-2xl font-700 text-white mb-2">Invitation unavailable</h1>
        <p className="text-slate-400 text-sm mb-6">{error}</p>
        <Link to="/login" className="text-brand-400 hover:text-brand-300 text-sm font-medium">
          Back to sign in
        </Link>
      </Shell>
    )
  }

  const facilityLabel = invite.facilities.map(f => f.name).join(', ')
  const inviterName   = invite.invited_by?.name || 'A teammate'

  return (
    <Shell>
      <h1 className="font-display text-2xl font-700 text-white mb-1">You've been invited</h1>
      <p className="text-slate-400 text-sm mb-6">
        <strong className="text-slate-200">{inviterName}</strong> invited you to join{' '}
        <strong className="text-slate-200">{facilityLabel}</strong> as a{' '}
        <strong className="text-brand-400">{invite.role}</strong>.
      </p>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          {error}
        </div>
      )}

      {invite.user_exists ? (
        // Existing user - either they're already logged in OR they need to log in first.
        user && user.email === invite.email ? (
          <>
            <p className="text-sm text-slate-300 mb-4">
              You're signed in as <strong>{user.email}</strong>. Click below to add these facilities
              to your account.
            </p>
            <button
              onClick={acceptAsExistingUser}
              disabled={submitting}
              className="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm transition-colors flex items-center justify-center disabled:opacity-60"
            >
              {submitting
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : 'Accept and continue'}
            </button>
          </>
        ) : (
          <>
            <div className="mb-4 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm text-amber-300">
              An account already exists for <strong>{invite.email}</strong>. Sign in with that
              account first, then return to this page to accept.
            </div>
            <Link
              to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
              className="inline-block w-full text-center py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm transition-colors"
            >
              Sign in to {invite.email}
            </Link>
          </>
        )
      ) : (
        // New email - collect name + password.
        <form onSubmit={submitNewUser} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Email</label>
            <input
              type="email" value={invite.email} disabled
              className="block w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Full name</label>
            <input
              type="text" value={form.name} required
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Your name"
              className="block w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'} required
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min 8 chars, mixed case + numbers"
                className="block w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition pr-10"
              />
              <button type="button" onClick={() => setShowPw(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Confirm password</label>
            <input
              type={showPw ? 'text' : 'password'} required
              value={form.password_confirmation}
              onChange={e => setForm(f => ({ ...f, password_confirmation: e.target.value }))}
              className="block w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
            />
          </div>

          <button
            type="submit" disabled={submitting}
            className="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm transition-colors flex items-center justify-center disabled:opacity-60"
          >
            {submitting
              ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : 'Accept and create account'}
          </button>
        </form>
      )}
    </Shell>
  )
}

// Reuses the same dark-shell visual language as Login/Register for consistency.
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8 hover:opacity-80 transition-opacity" aria-label="Go to Stovalt home">
          <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
            <Box size={16} className="text-white" />
          </div>
          <span className="font-display font-700 text-white text-lg">Stovalt</span>
        </Link>
        {children}
      </div>
    </div>
  )
}
